import React from "react";
import styled from "styled-components";
import { Modal as RSuiteModal, Divider } from "rsuite";
import { gradients } from "../theme/gradients";
import { shadows } from "../theme/shadows";
import { colors } from "../theme/colors";
import Button from "../Button/Button";
import CloseIcon from "@rsuite/icons/Close";

const Modal = ({
  children,
  title,
  open,
  onClose,
  size = "md",
  showFooter = true,
  showCloseButton = false,
  footerActions,
  loading = false,
  ...props
}) => {
  return (
    <StyledModal
      size={size}
      open={open}
      onClose={onClose}
      backdrop="static"
      className={`custom-modal-${size} ${showFooter ? "has-footer" : ""}`}
      {...props}
    >
      <RSuiteModal.Header closeButton={false}>
        <RSuiteModal.Title>{title}</RSuiteModal.Title>
        {showCloseButton && (
          <GlassCloseButton onClick={onClose}>
            <CloseIcon />
          </GlassCloseButton>
        )}
      </RSuiteModal.Header>
      <Divider />
      <RSuiteModal.Body>{children}</RSuiteModal.Body>

      {showFooter && (
        <RSuiteModal.Footer>
          <StyledFooterContent>
            {footerActions ? (
              footerActions
            ) : (
              <>
                <Button variant="subtle" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button variant="primary" loading={loading}>
                  Submit
                </Button>
              </>
            )}
          </StyledFooterContent>
        </RSuiteModal.Footer>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(RSuiteModal)`
  /* Remove any background shadows from modal wrapper */
  &.rs-modal {
    .rs-modal-wrapper {
      background: none !important;
      box-shadow: none !important;
    }

    .rs-modal-backdrop {
      background: rgba(0, 0, 0, 0.5) !important;
    }
  }

  .rs-modal-dialog {
    border-radius: 24px;
    overflow: hidden;
    box-shadow: ${shadows.xxl};
    max-width: 90vw;
    max-height: 75vh;
    margin: 12.5vh auto;
    display: flex !important;
    flex-direction: column !important;
    background: white !important;
    padding: 0 !important;
  }

  .rs-modal-content {
    border-radius: 24px;
    overflow: hidden;
  }

  &.rs-modal[size="xs"] {
    .rs-modal-dialog {
      max-width: 500px !important;
      width: 500px !important;
      height: fit-content !important;
      max-height: 75vh !important;
      min-height: auto !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
      margin: 10vh auto !important;
      position: relative;
      z-index: 1001;
      display: flex !important;
      flex-direction: column !important;
    }

    /* Double-check height override with higher specificity */
    .rs-modal-dialog.rs-modal-dialog {
      height: fit-content !important;
    }
  }

  /* Extra specific rule for custom class */
  &.custom-modal-xs.rs-modal .rs-modal-dialog {
    height: fit-content !important;
    min-height: fit-content !important;
  }

  &.custom-modal-xs.rs-modal[size="xs"] .rs-modal-dialog {
    height: fit-content !important;

    /* Remove any additional shadows or backgrounds */
    .rs-modal-wrapper {
      background: transparent !important;
      box-shadow: none !important;
    }

    /* Ensure backdrop is just the dark overlay */
    .rs-modal-backdrop {
      background: rgba(0, 0, 0, 0.5) !important;
      backdrop-filter: none !important;
    }

    /* Remove any translucent effects from modal body */
    .rs-modal-body {
      background: white !important;
      backdrop-filter: none !important;
    }

    /* Remove translucent effects from header */
    .rs-modal-header {
      backdrop-filter: none !important;
    }
  }

  &.rs-modal[size="sm"] .rs-modal-dialog {
    max-width: 650px;
    width: 650px;
  }

  &.rs-modal[size="md"] .rs-modal-dialog {
    max-width: 750px;
    width: 750px;
  }

  &.rs-modal[size="lg"] .rs-modal-dialog {
    max-width: 900px;
    width: 900px;
  }

  &.rs-modal[size="xl"] .rs-modal-dialog {
    max-width: 1200px;
    width: 1200px;
    max-height: 90vh !important;
    height: 90vh !important;
  }

  &.rs-modal[size="xl"] .rs-modal-body {
    max-height: calc(90vh - 200px) !important;
  }

  &.rs-modal[size="xl"].has-footer .rs-modal-body {
    max-height: calc(90vh - 20px) !important;
  }

  &.rs-modal[size="xxl"] .rs-modal-dialog {
    max-width: 1200px !important;
    width: 1000px !important;
    max-height: 95vh !important;
    height: 95vh !important;
  }

  &.rs-modal[size="xxl"] .rs-modal-body {
    flex: 1 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding: 24px !important;
    padding-top: 0px !important;
    max-height: calc(95vh - 150px) !important;
  }

  &.rs-modal[size="xxl"].has-footer .rs-modal-body {
    max-height: calc(95vh - 200px) !important;
  }

  .rs-modal-header {
    background: ${gradients.primary};
    color: white;
    border-radius: 24px 24px 0 0;
    padding: 20px 32px;
    flex-shrink: 0 !important;
    margin: 0 !important;
    position: relative;

    .rs-modal-title {
      color: white;
      font-weight: 600;
      font-size: 18px;
    }

    .rs-modal-header-close {
      color: white;
      opacity: 0.8;
      font-size: 20px;

      &:hover {
        opacity: 1;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
      }
    }
  }

  .rs-divider {
    margin: 0 !important;
    flex-shrink: 0 !important;
  }

  .rs-modal-body {
    padding: 24px !important;
    padding-top: 0px !important;
    flex: 1 1 auto !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    position: relative !important;
    min-height: 0 !important;
    max-height: calc(
      75vh - 200px
    ) !important; /* Constrain height to enable scrolling */

    /* Add fade gradient at bottom when content overflows */
    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 40px;
      background: linear-gradient(transparent, rgba(255, 255, 255, 0.3));
      pointer-events: none;
      z-index: 1;
      opacity: 0 !important;
      transition: opacity 0.3s ease;
    }

    /* Only show fade when content is actually scrollable */
    &:hover::after {
      opacity: 0 !important;
    }

    /* Alternative: Only show fade if scrolled or scrollable */
    &.scrollable::after {
      opacity: 0 !important;
    }

    &.scrollable:hover::after {
      opacity: 0 !important;
    }
  }

  /* When modal includes a footer ensure the body has extra bottom padding so content
     isn't obscured by the footer when scrolled */
  &.has-footer .rs-modal-body {
    padding-bottom: 24px !important;
    max-height: calc(75vh - 250px) !important; /* Account for footer height */
  }

  /* Special handling for xs modals with small content */
  &.rs-modal[size="xs"] .rs-modal-body {
    flex: 1 1 auto !important;
    max-height: calc(75vh - 200px) !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;

    /* Remove fade gradient for xs modals */
    &::after {
      display: none !important;
    }

    &:hover::after,
    &.scrollable::after,
    &.scrollable:hover::after {
      display: none !important;
    }
  }

  &.rs-modal[size="xs"].has-footer .rs-modal-body {
    max-height: calc(75vh - 250px) !important;
  }

  .rs-modal-footer {
    padding: 14px 32px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(12px);
    border-radius: 0 0 24px 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    flex-shrink: 0 !important;

    /* Make the footer sticky so it remains visible while the modal body scrolls.
       Use a high z-index so it sits above body content and shadows. */
    position: sticky !important;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 20 !important;
    display: block !important;
  }

  /* Fix footer background for xs modals */
  &.rs-modal[size="xs"] .rs-modal-footer {
    background: white !important;
    backdrop-filter: none !important;
    box-shadow: none !important;
    border-top: 1px solid #e0e0e0 !important;
  }

  .rs-form-control-wrapper {
    .rs-input {
      border-radius: 20px;
      border: 2px solid ${colors.neutral[200]};
      padding: 12px 20px;
      transition: all 0.3s ease;
      min-height: 48px !important;
      height: 48px;
      box-sizing: border-box;

      &:hover {
        border-color: ${colors.primary.main};
        box-shadow: ${shadows.hover.sm};
      }

      &:focus {
        border-color: ${colors.primary.main};
        box-shadow: ${shadows.focus};
      }
    }
  }

  .rs-form-group {
    margin-bottom: 4px;

    .rs-form-control-label {
      margin-bottom: 6px;
      display: block;
      font-weight: 500;
      font-size: 14px;
    }

    .rs-help-block {
      margin-bottom: 6px;
      font-size: 12px;
    }
  }

  .rs-picker-toggle {
    border-radius: 20px;
    border: 2px solid ${colors.neutral[200]};
    padding: 12px 20px;
    transition: all 0.3s ease;
    min-height: 48px !important;
    height: auto !important;
    display: flex;
    align-items: center;
    box-sizing: border-box;

    &:hover {
      border-color: ${colors.primary.main};
      box-shadow: ${shadows.hover.sm};
    }

    &:focus {
      border-color: ${colors.primary.main};
      box-shadow: ${shadows.focus};
    }
  }

  .rs-picker-menu {
    border-radius: 16px;
    border: none;
    box-shadow: ${shadows.lg};
    overflow: hidden;
    z-index: 9999;
    max-height: 300px;
  }
`;

const StyledFooterContent = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
`;

const GlassCloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 14px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    font-size: 20px;
  }
`;

export default Modal;
